import { useState, useEffect } from 'react';
import {
  Button,
  Cascader,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Upload,
  message,
  Tooltip,
} from 'antd';
import { UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import qs from 'query-string';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import AuthorizationInfo from '@/components/FormInfo/AuthorizationInfo';
import { getArea, getDictType } from '@/services/common';
import './index.less';
import {
  PROJECT_TYPE, BUSINESS_TYPE,
} from '@/utils/mappingDict';
import {
  getFinancingTypeSel, getIndustrySel, uploadFileUrl, addApply,
} from '@/services/transformationFinancing';
import { addDecimalNum } from '@/utils/utils';

const formItemLayout = {
  labelCol: {
    sm: { span: 8 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
};

// eslint-disable-next-line no-loss-of-precision
const maxNum = 9999999999999999;

const defaultParams = [PROJECT_TYPE, BUSINESS_TYPE].join(',');

export default function Application() {
  const [form] = Form.useForm();
  const Navigate = useNavigate();
  const { search } = useLocation();
  const { backId } = qs.parse(search);
  const { userType, userBasicInfo } = useSelector((state) => state);
  const user = useSelector((state) => state.userInfo);
  const [authInfoModal, setAuthInfoModal] = useState(false); // 授权书内容
  const [letterInfoModal, setLetterInfoModal] = useState(false); // 承诺函
  const { data: area = [] } = useRequest(getArea); // 区域
  const [agreementModal, setAgreementModal] = useState(false); // 接受协议

  const financialFunds = Form.useWatch('financialFunds', form); // 财政资金
  const selfOwnedFunds = Form.useWatch('selfOwnedFunds', form); // 资本金
  const totalLoanDemand = Form.useWatch('totalLoanDemand', form); // 贷款总需求

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        applicantName: user.displayName,
        applicantNo: user.displayCode,
        businessType: userBasicInfo.applicationUserBusinessInfoVo?.businessType,
        projectSum: 0,
      });
    }
  }, [user]);

  useEffect(() => {
    const projectSum = addDecimalNum([financialFunds, selfOwnedFunds, totalLoanDemand]);
    form.setFieldsValue({ projectSum });
  }, [financialFunds, selfOwnedFunds, totalLoanDemand]);

  const { data: dict = {} } = useRequest(getDictType, { // 筛选项字典
    defaultParams: [defaultParams],
  });

  // 融资需求类型
  const { data: financingType } = useRequest(getFinancingTypeSel);

  // 所属行业
  const { data: industrySel } = useRequest(getIndustrySel);

  const { loading, run } = useRequest(addApply, {
    manual: true,
    onSuccess() {
      Navigate('/result');
    },
  });

  const props = {
    name: 'file',
    accept: '.rar,.zip,.doc,.docx,.pdf',
    action: uploadFileUrl,
    headers: {
      authorization: 'authorization-text',
    },
    beforeUpload(file) {
      const isLt2M = file.size / 1024 / 1024 < 10;
      if (!isLt2M) {
        message.error('请上传10M以下文件!');
        return Upload.LIST_IGNORE;
      }
      return isLt2M;
    },
  };

  const onFileChange = ({ file, fileList }, key) => {
    if (isEmpty(fileList)) {
      form.setFieldsValue({ [key]: undefined });
    }
    // 过滤掉出错的文件，只保留非error状态的文件
    if (file.status === 'done') {
      message.success('上传成功');
      form.setFieldsValue({ [key]: fileList });
    } else if (file.status === 'error') {
      message.error('上传失败');
    }
  };

  useEffect(() => {
    if (userType !== 3) {
      Navigate('/home');
    }
  }, []);

  function onFinish(values) {
    if (values?.financialFunds > 0 && !isEmpty(values?.supportingFiles)) {
      // eslint-disable-next-line no-param-reassign
      values.supportingFiles = values.supportingFiles.map((item) => ({
        fileName: item.name,
        fileUrl: item.response.data,
      }));
    } else {
      // eslint-disable-next-line no-param-reassign
      values.supportingFiles = undefined;
    }
    if (!isEmpty(values?.appendixFiles)) {
      // eslint-disable-next-line no-param-reassign
      values.appendixFiles = values.appendixFiles.map((item) => ({
        fileName: item.name,
        fileUrl: item.response.data,
      }));
    } else {
      // eslint-disable-next-line no-param-reassign
      values.appendixFiles = undefined;
    }
    const obj = {
      ...values,
      provinceId: values.areaId[0],
      cityId: values.areaId[1],
      areaId: values.areaId[2],
      id: backId,
    };
    // 新申请
    run(obj);
  }

  function checkAuthorize(_, value) {
    const promise = Promise;
    if (!value) {
      return promise.reject('请勾选同意协议');
    }
    return promise.resolve();
  }

  return (
    <div className="major-financing-application">
      <div className="content-width">
        <div className="major-financing-application__title">转型项目融资申请</div>
        <div className="margin-top20">
          <Spin spinning={false}>
            <Form
              {...formItemLayout}
              form={form}
              name="form"
              className="form"
              onFinish={onFinish}
            >
              <div className="form-info__box">
                <div className="form-info__box-title">企业信息</div>
                <div className="form-info__box-con">
                  <Row>
                    <Col span={12}>
                      <Form.Item
                        name="applicantName"
                        label="企业名称"
                        rules={[
                          {
                            required: true,
                          },
                        ]}
                      >
                        <Input placeholder="请输入企业名称" disabled />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="applicantNo"
                        label="统一社会信用代码"
                        rules={[
                          {
                            required: true,
                          },
                        ]}
                      >
                        <Input placeholder="请输入统一社会信用代码" disabled />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="industryIdList"
                        label="所属行业"
                        rules={[
                          {
                            required: true,
                            message: '请选择所属行业',
                          },
                        ]}
                      >
                        <Cascader options={industrySel} placeholder="请选择所属行业" fieldNames={{ label: 'name', value: 'id', children: 'children' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="businessType"
                        label="主营业务类型"
                        rules={[
                          {
                            required: true,
                            message: '请选择主营业务类型',
                          },
                        ]}
                      >
                        <Select placeholder="请选择主营业务类型" options={dict[BUSINESS_TYPE]} fieldNames={{ label: 'name', value: 'val' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="form-info__box">
                <div className="form-info__box-title">项目信息</div>
                <div className="form-info__box-con">
                  <Row>
                    <Col span={12}>
                      <Form.Item
                        name="projectName"
                        label="项目名称"
                        rules={[
                          {
                            required: true,
                            message: '请输入项目名称',
                          },
                          { whitespace: true },
                          { max: 100 },
                        ]}
                      >
                        <Input placeholder="请输入项目名称" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="projectType"
                        label="建设性质"
                        rules={[
                          {
                            required: true,
                            message: '请选择建设性质',
                          },
                        ]}
                      >
                        <Select placeholder="请选择建设性质" options={dict?.projectType} fieldNames={{ label: 'name', value: 'val' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="areaId"
                        label="项目所在地"
                        rules={[
                          {
                            required: true,
                            message: '请选择项目所在地',
                          },
                        ]}
                      >
                        <Cascader options={area} placeholder="请选择项目所在地" fieldNames={{ label: 'name', value: 'id', children: 'children' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="selfOwnedFunds"
                        label="资本金"
                        rules={[
                          {
                            required: true,
                            message: '请输入资本金',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="请输入资本金"
                          addonAfter="万元"
                          min={0}
                          max={maxNum}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="financialFunds"
                        label="财政资金"
                        rules={[
                          {
                            required: true,
                            message: '请输入财政资金（如无，请填写0）',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="如无，请填写0"
                          addonAfter="万元"
                          min={0}
                          max={maxNum}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    {
                      financialFunds > 0 && (
                        <Col span={12}>
                          <Form.Item
                            name="supportingFiles"
                            label="财政投入佐证材料"
                            rules={[
                              {
                                required: true,
                                message: '请上传财政投入佐证材料',
                              },
                            ]}
                          >
                            <Upload {...props} onChange={(e) => onFileChange(e, 'supportingFiles')}>
                              <Button icon={<UploadOutlined />}>文件上传</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                      )
                    }
                    <Col span={12}>
                      <Form.Item
                        name="totalLoanDemand"
                        label="贷款总需求"
                        rules={[
                          { required: true, message: '请输入贷款总需求' },
                        ]}
                      >
                        <InputNumber
                          placeholder="请输入贷款总需求"
                          addonAfter="万元"
                          min={0}
                          max={maxNum}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="projectSum"
                        label={(
                              <span>
                              项目投资总额
                                <Tooltip title="项目投资总额 = 贷款总需求 + 财政资金 + 资本金">
                                  <QuestionCircleOutlined />
                                </Tooltip>
                              </span>
                              )}
                        rules={[
                          {
                            required: true,
                            message: '请输入项目投资总额',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="请输入项目投资总额"
                          addonAfter="万元"
                          min={0}
                          max={maxNum}
                          disabled
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="capitalTerm"
                        label="资金使用期限"
                        rules={[
                          { required: true, message: '请输入资金使用期限' },
                        ]}
                      >
                        <InputNumber
                          placeholder="请输入资金使用期限"
                          addonAfter="月"
                          min={1}
                          max={999}
                          precision={0}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="contactPerson"
                        label="联系人"
                        rules={[
                          { required: true, message: '请输入联系人' },
                          { whitespace: true },
                          { max: 100 },
                        ]}
                      >
                        <Input placeholder="请输入联系人" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="contactTel"
                        label="联系方式"
                        rules={[
                          { required: true, message: '请输入联系方式' },
                          { whitespace: true },
                          {
                            pattern: /^1\d{10}$/,
                            message: '请输入11位有效手机号',
                          },
                        ]}
                      >
                        <Input placeholder="请输入联系方式" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="financingIdList"
                        label={(
                          <span>
                              融资需求类型
                                <Tooltip title="融资需求类型按《转型金融支持活动目录（农业）》内“技术/标准/说明”进一步拆分，为转型内容或路径下的具体活动，用户可初步判断其项目可能对应的融资需求类型并填报。">
                                  <QuestionCircleOutlined />
                                </Tooltip>
                          </span>
                        )}
                        rules={[
                          {
                            required: true,
                            message: '请选择融资需求类型',
                          },
                        ]}
                      >
                        <Cascader options={financingType} placeholder="请选择融资需求类型" fieldNames={{ label: 'name', value: 'id', children: 'children' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="projectContent"
                        label="项目建设内容"
                        rules={[
                          { required: true, message: '请输入项目建设内容' },
                          { whitespace: true },
                        ]}
                      >
                        <Input.TextArea maxLength={500} showCount placeholder="请输入项目建设内容" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="appendixFiles"
                        label="附件"
                      >
                        <Upload {...props} onChange={(e) => onFileChange(e, 'appendixFiles')}>
                          <Button icon={<UploadOutlined />}>上传文件</Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="authorize">
                <Form.Item
                  name="authorize"
                  wrapperCol={{ span: 24 }}
                  valuePropName="checked"
                  rules={[
                    { validator: checkAuthorize },
                  ]}

                >
                  <Checkbox
                    onChange={(e) => {
                      form.setFieldsValue({ authorize: false });
                      if (e.target.checked) {
                        setAgreementModal(true);
                      }
                    }}
                  >
                    同意并接受
                    <Button type="link" className="authorize-name" onClick={() => setAuthInfoModal(true)} style={{ paddingRight: 0 }}>《直通车平台业务授权协议》、</Button>
                    <Button type="link" className="authorize-name" onClick={() => setLetterInfoModal(true)} style={{ paddingLeft: 0 }}>《信息真实性承诺函》</Button>
                  </Checkbox>
                </Form.Item>
              </div>
              <div className="submit-btn-box">
                <Button type="primary" htmlType="submit" loading={loading}>
                  提交申请
                </Button>
              </div>
            </Form>
          </Spin>
          <Modal title="授权协议查看" open={authInfoModal} width={900} footer={null} onCancel={() => setAuthInfoModal(false)}>
            <div dangerouslySetInnerHTML={{ __html: AuthorizationInfo }} style={{ maxHeight: 600, overflowY: 'scroll' }} />
          </Modal>
          <Modal
            title="承诺函"
            open={letterInfoModal}
            footer={
              [
                <Button
                  type="primary"
                  key="confirm"
                  onClick={() => {
                    setLetterInfoModal(false);
                    // form.setFieldsValue({ authorize: true });
                  }}
                >
                  确认
                </Button>,
              ]
            }
            onCancel={() => setLetterInfoModal(false)}
          >
            <div>
              本人（企业、合作社） 承诺在申报时提交的所有材料真实、合法、有效，若发现材料存在虚假、瞒报等情况， 本人（企业、合作社）自愿放弃本次申报，并承担违规违法责任。
            </div>
          </Modal>
          <Modal
            title="授权协议及承诺函"
            open={agreementModal}
            onCancel={() => {
              setAgreementModal(false);
              form.setFieldsValue({ authorize: false });
            }}
            onOk={() => {
              setAgreementModal(false);
              form.setFieldsValue({ authorize: true });
            }}
          >
            <div>
              为更好地保护您的合法权益，请阅读并同意《直通车平台业务授权协议》和《信息真实性承诺函》。
            </div>
          </Modal>
        </div>
      </div>

    </div>
  );
}
